import React, { useState, useEffect, useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { db } from "../firebaseConfig";
import { collection, doc, getDocs, updateDoc } from "firebase/firestore";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  Label,
} from "recharts";
import { writeFile, utils } from "xlsx";
import { useTheme } from "../ThemeContext";
import HeaderActionsMinimal from "./HeaderActionsMinimal";
import { evaluateProjectStatuses } from "../utils/status";
import { buildTasksSearchText } from "../utils/tasksSearchText";

// ==================== ICON COMPONENTS ====================
const TrendingUpIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
    />
  </svg>
);

const TrendingDownIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
    />
  </svg>
);

const WalletIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
    />
  </svg>
);

const ChartIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const UsersIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </svg>
);

const CubeIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
    />
  </svg>
);

const MapPinIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

const ClipboardIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
    />
  </svg>
);

const DollarSignIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const DownloadIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
    />
  </svg>
);

// ==================== HELPER FUNCTIONS ====================
const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

const formatNumber = (amount) =>
  new Intl.NumberFormat("en-GB", {
    maximumFractionDigits: 0,
  }).format(amount);

const ensureNumber = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

const normalizeCountry = (country) => {
  if (!country) return "Unknown";
  const trimmed = country.trim();
  const upper = trimmed.toUpperCase();
  if (upper === "UAE" || upper.startsWith("UAE-") || upper.startsWith("UAE -"))
    return "UAE";
  if (upper === "UNITED ARAB EMIRATES") return "UAE";
  return trimmed;
};

const normalizeCompany = (company) => {
  if (!company) return "Unassigned";
  const trimmed = company.trim();
  if (!trimmed) return "Unassigned";
  const upper = trimmed.toUpperCase();
  if (upper === "UK") return "UK";
  if (upper === "UAE") return "UAE";
  return trimmed;
};

const circleFlagCodeMap = {
  "UK": "gb",
  "UNITED KINGDOM": "gb",
  "UAE": "ae",
  "UNITED ARAB EMIRATES": "ae",
  "UAE - ABU DHABI": "ae",
  "UAE - AJMAN": "ae",
  "UAE - DUBAI": "ae",
  "UAE - FUJAIRAH": "ae",
  "UAE - RAK": "ae",
  "UAE - SHARJAH": "ae",
  "UAE - UAQ": "ae",
  "USA": "us",
  "UNITED STATES": "us",
  "SAUDI ARABIA": "sa",
  "QATAR": "qa",
  "OMAN": "om",
  "KUWAIT": "kw",
  "BAHRAIN": "bh",
  "EGYPT": "eg",
  "MOROCCO": "ma",
  "ALGERIA": "dz",
  "TUNISIA": "tn",
  "LIBYA": "ly",
  "IRAQ": "iq",
  "IRAN": "ir",
  "PAKISTAN": "pk",
  "AFGHANISTAN": "af",
  "DJIBOUTI": "dj",
  "MAURITANIA": "mr",
  "SOMALIA": "so",
  "SUDAN": "sd",
  "YEMEN": "ye",
  "TURKEY": "tr",
  "LEBANON": "lb",
  "JORDAN": "jo",
  "PALESTINE": "ps",
  "SYRIA": "sy",
  "INDIA": "in",
  "SOUTH AFRICA": "za",
  "FRANCE": "fr",
  "GERMANY": "de",
  "SPAIN": "es",
  "ITALY": "it",
  "NETHERLANDS": "nl",
  "SWITZERLAND": "ch",
  "BELGIUM": "be",
  "NORWAY": "no",
  "DENMARK": "dk",
  "FINLAND": "fi",
  "SWEDEN": "se",
  "CANADA": "ca",
  "AUSTRALIA": "au",
  "NEW ZEALAND": "nz",
  "SINGAPORE": "sg",
  "MEXICO": "mx",
  "BRAZIL": "br",
  "CHINA": "cn",
  "JAPAN": "jp",
  "RUSSIA": "ru",
};

const getCircleFlagCode = (countryOrCompany) => {
  if (!countryOrCompany) return "xx";
  const normalized = countryOrCompany.trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(normalized)) return normalized.toLowerCase();
  return circleFlagCodeMap[normalized] || "xx";
};

const getCircleFlagUrl = (countryOrCompany) =>
  `https://hatscripts.github.io/circle-flags/flags/${getCircleFlagCode(
    countryOrCompany
  )}.svg`;

const CountryFlagIcon = ({ country, size = 18, className = "" }) => (
  <img
    src={getCircleFlagUrl(country)}
    alt={country ? `${country} flag` : "Flag"}
    width={size}
    height={size}
    className={`flex-shrink-0 ${className}`}
    style={{
      borderRadius: "50%",
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
      border: "1px solid rgba(15, 23, 42, 0.08)",
      backgroundColor: "#fff",
    }}
    loading="lazy"
  />
);

const getCompanyLabel = (company) => {
  const labels = {
    All: "All Companies",
    UK: "Fort Corps Limited",
    UAE: "Fort Core LLC FZ",
    Unassigned: "Company not assigned",
  };
  return labels[company] || company || "Company not assigned";
};

const generateColors = (count) => {
  const base = [
    "#6366F1",
    "#06B6D4",
    "#10B981",
    "#F59E0B",
    "#EF4444",
    "#8B5CF6",
    "#EC4899",
    "#14B8A6",
    "#F97316",
    "#84CC16",
    "#3B82F6",
    "#78716C",
  ];
  if (count <= base.length) return base.slice(0, count);
  const extended = [...base];
  const rounds = Math.ceil(count / base.length);
  for (let i = 1; i < rounds; i++) {
    const opacity = 1 - i * 0.15;
    base.forEach((hex) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      extended.push(`rgba(${r}, ${g}, ${b}, ${opacity})`);
    });
  }
  return extended.slice(0, count);
};

const makePieData = (
  rows,
  {
    nameKey = "name",
    valueKey = "total",
    maxSlices = 8,
    minPercent = 0.035,
  } = {}
) => {
  const sorted = [...rows].sort(
    (a, b) => (b[valueKey] || 0) - (a[valueKey] || 0)
  );
  const total = sorted.reduce((s, r) => s + (r[valueKey] || 0), 0) || 1;

  const keep = [];
  const other = { [nameKey]: "Other", [valueKey]: 0 };

  sorted.forEach((r, i) => {
    const pct = (r[valueKey] || 0) / total;
    if (i < maxSlices - 1 && pct >= minPercent) {
      keep.push(r);
    } else {
      other[valueKey] += r[valueKey] || 0;
    }
  });

  const out = other[valueKey] > 0 ? [...keep, other] : keep;
  return out.map((r) => ({
    ...r,
    _percent: ((r[valueKey] || 0) / total) * 100,
  }));
};

// ==================== REUSABLE COMPONENTS ====================
const AnimatedNumber = ({ value, duration = 1000, format = "currency" }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const startTime = Date.now();
    const startValue = displayValue;
    const endValue = value;

    const animate = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      const easeInOut =
        progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      const currentValue = startValue + (endValue - startValue) * easeInOut;
      setDisplayValue(currentValue);
      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [value]); // eslint-disable-line

  if (typeof format === "function") return format(displayValue);
  if (format === "currency") return formatCurrency(displayValue);
  if (format === "percent") return `${displayValue.toFixed(1)}%`;
  if (format === "number") return formatNumber(displayValue);

  return displayValue.toFixed(0);
};

const SummaryCard = ({
  title,
  value,
  icon,
  color,
  progressPercent,
  format = "currency",
}) => (
  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-200">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-medium text-gray-600">{title}</h3>
      <div className={`bg-${color}-50 p-2 rounded-lg`}>{icon}</div>
    </div>
    <p className="text-2xl font-semibold text-gray-900 mb-1">
      <AnimatedNumber value={value} format={format} />
    </p>
    {progressPercent !== undefined && (
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full bg-${color}-500 rounded-full transition-all duration-1000`}
            style={{ width: `${Math.min(progressPercent, 100)}%` }}
          />
        </div>
        <span className="text-xs font-medium text-gray-600">
          {progressPercent.toFixed(1)}%
        </span>
      </div>
    )}
  </div>
);

const CustomTooltip = ({
  active,
  payload = [],
  label,
  format = "currency",
}) => {
  if (!active || !payload.length) return null;

  const formatValue = (value) => {
    if (format === "number") return formatNumber(value);
    return formatCurrency(value);
  };

  return (
    <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
      <p className="font-semibold text-gray-900 mb-2">{label}</p>
      <div className="space-y-1">
        {payload.map((entry, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between gap-4 text-sm"
          >
            <span className="flex items-center gap-2 text-gray-700">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ background: entry.color }}
              />
              {entry.name}:
            </span>
            <span className="font-semibold" style={{ color: entry.color }}>
              {formatValue(entry.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const PieSideLegend = ({
  data,
  colors,
  nameKey = "name",
  valueKey = "total",
}) => {
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      {data.map((d, i) => (
        <div
          key={i}
          className="flex items-center gap-2 text-sm px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-full"
          title={`${d[nameKey]}: ${formatCurrency(d[valueKey] || 0)}`}
        >
          <span
            className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
            style={{ background: colors[i] }}
          />
          <span className="truncate max-w-[120px] text-gray-800">
            {d[nameKey]}
          </span>
        </div>
      ))}
    </div>
  );
};

const truncateLabel = (str, max = 10) => {
  if (!str) return "";
  return str.length > max ? `${str.substring(0, max)}...` : str;
};

// ==================== MAIN ANALYTICS COMPONENT ====================
const Analytics = () => {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Filter state
  const [selectedYear, setSelectedYear] = useState("All");
  const [selectedClient, setSelectedClient] = useState("All");
  const [selectedCompany, setSelectedCompany] = useState("All");
  const [selectedCountry, setSelectedCountry] = useState("All");
  const [selectedType, setSelectedType] = useState("All");

  // View state
  const [viewMode, setViewMode] = useState("overview");
  const [isAnimating, setIsAnimating] = useState(false);
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);

  // Data state
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const { theme } = useTheme();

  // Fetch data from Firestore
  useEffect(() => {
    const fetchData = async () => {
      try {
        const projectsCollection = collection(db, "projects");
        const snapshot = await getDocs(projectsCollection);
        const data = [];
        const updatePromises = [];

        for (const projectDoc of snapshot.docs) {
          const rawProject = { id: projectDoc.id, ...projectDoc.data() };
          const {
            updatedTasks,
            projectStatus,
            invoiceRaised,
            paymentStatus,
          } = evaluateProjectStatuses(rawProject);

          const originalTasks = rawProject.tasks || [];
          const tasksChanged =
            originalTasks.length !== updatedTasks.length ||
            updatedTasks.some(
              (task, index) =>
                task.taskStatus !==
                (originalTasks[index] && originalTasks[index].taskStatus)
            );

          const updates = {};
          if (tasksChanged) {
            updates.tasks = updatedTasks;
            updates.tasksSearchText = buildTasksSearchText(updatedTasks);
          }
          if (rawProject.projectStatus !== projectStatus) {
            updates.projectStatus = projectStatus;
          }
          if (rawProject.invoiceRaised !== invoiceRaised) {
            updates.invoiceRaised = invoiceRaised;
          }
          if (rawProject.paymentStatus !== paymentStatus) {
            updates.paymentStatus = paymentStatus || null;
          }

          if (Object.keys(updates).length > 0) {
            updatePromises.push(
              updateDoc(doc(db, "projects", rawProject.id), updates)
            );
          }

          data.push({
            ...rawProject,
            tasks: updatedTasks,
            projectStatus,
            invoiceRaised,
            paymentStatus,
          });
        }

        if (updatePromises.length) {
          await Promise.allSettled(updatePromises);
        }

        setAllData(data);
        setFilteredData(data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = allData;

    if (selectedYear !== "All") {
      const targetYear = selectedYear.toString();
      filtered = filtered.filter((project) =>
        project.tasks?.some((task) => {
          if (!task.orderAccepted) return false;
          const date = new Date(task.orderAccepted);
          return !isNaN(date) && date.getFullYear().toString() === targetYear;
        })
      );
    }

    if (selectedClient !== "All") {
      filtered = filtered.filter((item) => item.client === selectedClient);
    }

    if (selectedCompany !== "All") {
      filtered = filtered.filter(
        (item) => normalizeCompany(item.company) === selectedCompany
      );
    }

    if (selectedCountry !== "All") {
      const normalizedSelectedCountry = normalizeCountry(selectedCountry);
      filtered = filtered.filter((item) =>
        (item.tasks || []).some(
          (task) => normalizeCountry(task.country) === normalizedSelectedCountry
        )
      );
    }

    if (selectedType !== "All") {
      filtered = filtered.filter((item) =>
        (item.tasks || []).some((task) => task.task === selectedType)
      );
    }

    setFilteredData(filtered);
  }, [
    selectedClient,
    selectedCompany,
    selectedCountry,
    selectedType,
    selectedYear,
    allData,
  ]);

  // Available filter options
  const companyOptions = ["UK", "UAE", "Unassigned"];
  const availableYears = [
    ...new Set(
      allData
        .flatMap((project) =>
          (project.tasks || []).map((task) => {
            if (!task.orderAccepted) return null;
            const parsedDate = new Date(task.orderAccepted);
            if (isNaN(parsedDate.getTime())) return null;
            const year = parsedDate.getFullYear();
            if (year < 2000 || year > new Date().getFullYear() + 1) return null;
            return year;
          })
        )
        .filter((year) => year !== null)
    ),
  ].sort((a, b) => a - b);

  const taskMatchesFilters = (task) => {
    if (selectedType !== "All" && task.task !== selectedType) return false;

    if (selectedCountry !== "All") {
      const normalizedSelectedCountry = normalizeCountry(selectedCountry);
      if (normalizeCountry(task.country) !== normalizedSelectedCountry)
        return false;
    }

    return true;
  };

  const filteredTasksForProject = (project) =>
    (project.tasks || []).filter((task) => taskMatchesFilters(task));

  // ==================== DATA TRANSFORMATION FUNCTIONS ====================
  const prepareMonthlyData = () => {
    const monthlyTotals = Array(12)
      .fill(null)
      .map(() => ({
        income: 0,
        vat: 0,
        incomeIncVat: 0,
        cost: 0,
        profit: 0,
        profitMargin: 0,
      }));

    filteredData.forEach((project) => {
      if (!project.tasks) return;

      const projectCost = ensureNumber(project.cost);
      const tasks = filteredTasksForProject(project);
      const costBearingTasks = tasks.filter((task) => task.orderAccepted);
      const costPerTask =
        costBearingTasks.length > 0 ? projectCost / costBearingTasks.length : 0;

      tasks.forEach((task) => {
        if (!task.orderAccepted) return;

        const date = new Date(task.orderAccepted);
        const monthIndex = date.getMonth();

        const income = ensureNumber(task.income);
        const vatRate = ensureNumber(task.vat);

        const vat = (income * vatRate) / 100;
        const incomeIncVat = income + vat;

        monthlyTotals[monthIndex].income += income;
        monthlyTotals[monthIndex].vat += vat;
        monthlyTotals[monthIndex].incomeIncVat += incomeIncVat;
        monthlyTotals[monthIndex].cost += costPerTask;
      });
    });

    monthlyTotals.forEach((month) => {
      month.profit = month.income - month.cost;
      month.profitMargin =
        month.income > 0 ? (month.profit / month.income) * 100 : 0;
    });

    return monthlyTotals.map((data, idx) => ({
      month: months[idx],
      ...data,
    }));
  };

  const prepareTaskTypeData = () => {
    const taskTypes = {};

    filteredData.forEach((project) => {
      const projectCost = ensureNumber(project.cost);
      const tasks = filteredTasksForProject(project);
      const costBearingTasks = tasks.filter((task) => task.orderAccepted);
      const costPerTask =
        costBearingTasks.length > 0 ? projectCost / costBearingTasks.length : 0;

      tasks.forEach((task) => {
        const type = task.task || "Unknown";
        if (!taskTypes[type]) {
          taskTypes[type] = {
            type,
            income: 0,
            cost: 0,
            count: 0,
          };
        }

        taskTypes[type].income += ensureNumber(task.income);
        taskTypes[type].cost += task.orderAccepted ? costPerTask : 0;
        taskTypes[type].count += 1;
      });
    });

    return Object.values(taskTypes)
      .map((type) => ({
        ...type,
        profit: type.income - type.cost,
        profitMargin:
          type.income > 0 ? ((type.income - type.cost) / type.income) * 100 : 0,
        avgIncome: type.count > 0 ? type.income / type.count : 0,
      }))
      .sort((a, b) => b.income - a.income);
  };

  const prepareClientData = () => {
    const clients = {};

    filteredData.forEach((project) => {
      const client = project.client || "Unknown";
      if (!clients[client]) {
        clients[client] = {
          name: client,
          income: 0,
          cost: 0,
          projectCount: 0,
          taskCount: 0,
        };
      }

      clients[client].projectCount += 1;

      const projectCost = ensureNumber(project.cost);
      const tasks = filteredTasksForProject(project);
      const costBearingTasks = tasks.filter((task) => task.orderAccepted);
      const costPerTask =
        costBearingTasks.length > 0 ? projectCost / costBearingTasks.length : 0;

      tasks.forEach((task) => {
        clients[client].income += ensureNumber(task.income);
        clients[client].cost += task.orderAccepted ? costPerTask : 0;
        clients[client].taskCount += 1;
      });
    });

    return Object.values(clients)
      .map((client) => ({
        ...client,
        profit: client.income - client.cost,
        profitMargin:
          client.income > 0
            ? ((client.income - client.cost) / client.income) * 100
            : 0,
        total: client.income,
      }))
      .sort((a, b) => b.income - a.income);
  };

  const prepareCountryData = () => {
    const countries = {};

    filteredData.forEach((project) => {
      const projectCost = ensureNumber(project.cost);
      const tasks = filteredTasksForProject(project);
      const costBearingTasks = tasks.filter((task) => task.orderAccepted);
      const costPerTask =
        costBearingTasks.length > 0 ? projectCost / costBearingTasks.length : 0;

      tasks.forEach((task) => {
        const country = normalizeCountry(task.country) || "Unknown";
        if (!countries[country]) {
          countries[country] = { name: country, income: 0, cost: 0, count: 0 };
        }

        countries[country].income += ensureNumber(task.income);
        countries[country].cost += task.orderAccepted ? costPerTask : 0;
        countries[country].count += 1;
      });
    });

    return Object.values(countries)
      .map((country) => ({
        ...country,
        profit: country.income - country.cost,
        total: country.income,
      }))
      .sort((a, b) => b.income - a.income);
  };

  const prepareTaskCompletionData = () => {
    let onTime = 0,
      overdue = 0,
      inProgress = 0;
    let totalCompletionDays = 0,
      completedCount = 0;

    filteredData.forEach((project) => {
      const tasks = filteredTasksForProject(project);
      tasks.forEach((task) => {
        if (!task.completedOn) {
          inProgress += 1;
          return;
        }

        const dueDate = new Date(task.dueDate);
        const completedDate = new Date(task.completedOn);
        const orderDate = new Date(task.orderAccepted);

        completedCount += 1;
        totalCompletionDays +=
          (completedDate - orderDate) / (1000 * 60 * 60 * 24);

        if (completedDate <= dueDate) onTime += 1;
        else overdue += 1;
      });
    });

    return {
      distribution: [
        { name: "On Time", value: onTime, fill: "#10B981" },
        { name: "Overdue", value: overdue, fill: "#EF4444" },
        { name: "In Progress", value: inProgress, fill: "#F59E0B" },
      ],
      metrics: {
        onTimeRate:
          onTime + overdue > 0 ? (onTime / (onTime + overdue)) * 100 : 0,
        avgCompletionDays:
          completedCount > 0 ? totalCompletionDays / completedCount : 0,
      },
    };
  };

  const prepareProjectStatusData = () => {
    const statuses = {};

    filteredData.forEach((project) => {
      const status = project.projectStatus || "Unknown";
      statuses[status] = (statuses[status] || 0) + 1;
    });

    const statusColors = {
      "In Progress": "#10B981",
      Completed: "#3B82F6",
      Overdue: "#EF4444",
      Attention: "#F59E0B",
      "To be invoiced": "#8B5CF6",
      Invoiced: "#06B6D4",
      Unknown: "#78716C",
    };

    return Object.entries(statuses).map(([name, value]) => ({
      name,
      value,
      fill: statusColors[name] || "#78716C",
    }));
  };

  const prepareAnalystData = () => {
    const analysts = {};

    filteredData.forEach((project) => {
      const tasks = filteredTasksForProject(project);
      tasks.forEach((task) => {
        const uniqueAnalysts = [
          ...new Set([task.analyst1, task.analyst2].filter(Boolean)),
        ];
        const split = uniqueAnalysts.length || 1;

        uniqueAnalysts.forEach((analyst) => {
          if (!analysts[analyst]) {
            analysts[analyst] = {
              name: analyst,
              income: 0,
              taskCount: 0,
              completedCount: 0,
            };
          }

          analysts[analyst].income += ensureNumber(task.income) / split;
          analysts[analyst].taskCount += 1;

          if (task.completedOn) {
            analysts[analyst].completedCount += 1;
          }
        });
      });
    });

    return Object.values(analysts)
      .map((analyst) => ({
        ...analyst,
        avgIncome:
          analyst.taskCount > 0 ? analyst.income / analyst.taskCount : 0,
        completionRate:
          analyst.taskCount > 0
            ? (analyst.completedCount / analyst.taskCount) * 100
            : 0,
      }))
      .sort((a, b) => b.income - a.income);
  };

  const prepareInvoiceMetrics = () => {
    let totalDaysToPayment = 0;
    let paidInvoiceCount = 0;
    let outstandingInvoices = 0;

    filteredData.forEach((project) => {
      if (project.invoiceRaised === "Yes") {
        if (project.paidOn && project.paidOn !== "N/A") {
          const invoiceDate = new Date(project.dateForInvoice);
          const paidDate = new Date(project.paidOn);
          const days = (paidDate - invoiceDate) / (1000 * 60 * 60 * 24);
          totalDaysToPayment += days;
          paidInvoiceCount += 1;
        } else {
          outstandingInvoices += 1;
        }
      }
    });

    return {
      avgDaysToPayment:
        paidInvoiceCount > 0 ? totalDaysToPayment / paidInvoiceCount : 0,
      outstandingCount: outstandingInvoices,
      paidCount: paidInvoiceCount,
    };
  };

  // Computed data with useMemo for performance
  const monthlyData = useMemo(() => prepareMonthlyData(), [filteredData]); // eslint-disable-line
  const taskTypeData = useMemo(() => prepareTaskTypeData(), [filteredData]); // eslint-disable-line
  const clientData = useMemo(() => prepareClientData(), [filteredData]); // eslint-disable-line
  const countryData = useMemo(() => prepareCountryData(), [filteredData]); // eslint-disable-line
  const taskCompletionData = useMemo(
    () => prepareTaskCompletionData(),
    [filteredData]
  ); // eslint-disable-line
  const projectStatusData = useMemo(
    () => prepareProjectStatusData(),
    [filteredData]
  ); // eslint-disable-line
  const analystData = useMemo(() => prepareAnalystData(), [filteredData]); // eslint-disable-line
  const invoiceMetrics = useMemo(() => prepareInvoiceMetrics(), [filteredData]); // eslint-disable-line

  // Summary statistics
  const totalIncome = monthlyData.reduce((sum, month) => sum + month.income, 0);
  const totalCost = monthlyData.reduce((sum, month) => sum + month.cost, 0);
  const totalProfit = totalIncome - totalCost;
  const totalProfitMargin =
    totalIncome > 0 ? (totalProfit / totalIncome) * 100 : 0;
  const totalProjects = filteredData.length;
  const totalTasks = filteredData.reduce(
    (sum, p) => sum + filteredTasksForProject(p).length,
    0
  );

  // Export function
  const exportToExcel = () => {
    const roundCurrency = (value) => Math.round(ensureNumber(value));
    const roundPercent = (value) =>
      Math.round(ensureNumber(value) * 10) / 10;

    const workbook = utils.book_new();

    // Overview sheet (filters + headline metrics + project status)
    const overviewRows = [
      ["Filters"],
      ["Year", selectedYear],
      ["Client", selectedClient],
      ["Company", getCompanyLabel(selectedCompany)],
      ["Country", selectedCountry],
      ["Type", selectedType],
      [],
      ["Overview Metrics"],
      ["Total Revenue (GBP)", roundCurrency(totalIncome)],
      ["Total Costs (GBP)", roundCurrency(totalCost)],
      ["Gross Profit (GBP)", roundCurrency(totalProfit)],
      ["Profit Margin %", roundPercent(totalProfitMargin)],
      ["Projects", totalProjects],
      ["Tasks", totalTasks],
      [],
      ["Project Status"],
      ["Status", "Count"],
      ...projectStatusData.map((status) => [status.name, status.value]),
    ];
    const overviewSheet = utils.aoa_to_sheet(overviewRows);
    utils.book_append_sheet(workbook, overviewSheet, "Overview");

    // Financial sheet (monthly + invoice metrics)
    const financialHeader = [
      "Month",
      "Income",
      "VAT",
      "Income inc VAT",
      "Cost",
      "Profit",
      "Profit Margin %",
    ];
    const financialRows = monthlyData.map((month) => ({
      Month: month.month,
      Income: roundCurrency(month.income),
      VAT: roundCurrency(month.vat),
      "Income inc VAT": roundCurrency(month.incomeIncVat),
      Cost: roundCurrency(month.cost),
      Profit: roundCurrency(month.profit),
      "Profit Margin %": roundPercent(month.profitMargin),
    }));
    const financialSheet = utils.json_to_sheet(financialRows, {
      header: financialHeader,
    });
    utils.sheet_add_aoa(
      financialSheet,
      [
        [],
        ["Invoice Metrics"],
        ["Avg Days to Payment", roundCurrency(invoiceMetrics.avgDaysToPayment)],
        ["Paid Invoices", invoiceMetrics.paidCount],
        ["Outstanding Invoices", invoiceMetrics.outstandingCount],
      ],
      { origin: `A${financialRows.length + 3}` }
    );
    utils.book_append_sheet(workbook, financialSheet, "Financial");

    // Task Analytics sheet (completion + task types + analysts)
    const taskSummaryRows = [
      ["Task Completion Summary"],
      ["On-Time Rate %", roundPercent(taskCompletionData.metrics.onTimeRate)],
      [
        "Avg Completion Days",
        roundCurrency(taskCompletionData.metrics.avgCompletionDays),
      ],
      [],
      ["Completion Breakdown"],
      ["Status", "Count"],
      ...taskCompletionData.distribution.map((item) => [item.name, item.value]),
    ];
    const taskSheetActual = utils.aoa_to_sheet(taskSummaryRows);
    const taskTypeHeader = [
      "Task Type",
      "Tasks",
      "Income",
      "Cost",
      "Profit",
      "Profit Margin %",
      "Avg Income / Task",
    ];
    const taskTypeRows = taskTypeData.map((type) => ({
      "Task Type": type.type,
      Tasks: type.count,
      Income: roundCurrency(type.income),
      Cost: roundCurrency(type.cost),
      Profit: roundCurrency(type.profit),
      "Profit Margin %": roundPercent(type.profitMargin),
      "Avg Income / Task": roundCurrency(type.avgIncome),
    }));
    utils.sheet_add_json(taskSheetActual, taskTypeRows, {
      header: taskTypeHeader,
      origin: `A${taskSummaryRows.length + 2}`,
    });
    const analystHeader = [
      "Analyst",
      "Tasks",
      "Revenue",
      "Avg / Task",
      "Completion %",
    ];
    const analystRows = analystData.map((analyst) => ({
      Analyst: analyst.name,
      Tasks: analyst.taskCount,
      Revenue: roundCurrency(analyst.income),
      "Avg / Task": roundCurrency(analyst.avgIncome),
      "Completion %": roundPercent(analyst.completionRate),
    }));
    utils.sheet_add_json(taskSheetActual, analystRows, {
      header: analystHeader,
      origin: `A${taskSummaryRows.length + taskTypeRows.length + 6}`,
    });
    utils.book_append_sheet(workbook, taskSheetActual, "Task Analytics");

    // Clients sheet
    const clientsHeader = [
      "Client",
      "Projects",
      "Tasks",
      "Revenue",
      "Cost",
      "Profit",
      "Margin %",
    ];
    const clientsRows = clientData.map((client) => ({
      Client: client.name,
      Projects: client.projectCount,
      Tasks: client.taskCount,
      Revenue: roundCurrency(client.income),
      Cost: roundCurrency(client.cost),
      Profit: roundCurrency(client.profit),
      "Margin %": roundPercent(client.profitMargin),
    }));
    const clientsSheet = utils.json_to_sheet(clientsRows, {
      header: clientsHeader,
    });
    utils.book_append_sheet(workbook, clientsSheet, "Clients");

    // Geography sheet
    const geographyHeader = [
      "Country",
      "Tasks",
      "Revenue",
      "Cost",
      "Profit",
    ];
    const geographyRows = countryData.map((country) => ({
      Country: country.name,
      Tasks: country.count,
      Revenue: roundCurrency(country.income),
      Cost: roundCurrency(country.cost),
      Profit: roundCurrency(country.profit),
    }));
    const geographySheet = utils.json_to_sheet(geographyRows, {
      header: geographyHeader,
    });
    utils.book_append_sheet(workbook, geographySheet, "Geography");

    // Raw data sheet (filtered tasks with project context)
    const rawRows = [];
    filteredData.forEach((project) => {
      const projectCost = ensureNumber(project.cost);
      const tasks = filteredTasksForProject(project);
      const costBearingTasks = tasks.filter((task) => task.orderAccepted);
      const costPerTask =
        costBearingTasks.length > 0
          ? projectCost / costBearingTasks.length
          : 0;

      tasks.forEach((task) => {
        const income = ensureNumber(task.income);
        const vatRate = ensureNumber(task.vat);
        const vatAmount = (income * vatRate) / 100;
        const allocatedCost = task.orderAccepted ? costPerTask : 0;

        rawRows.push({
          ProjectRef: project.projectRef || project.id || "",
          Client: project.client || "",
          Company: normalizeCompany(project.company || "Unassigned"),
          ProjectStatus: project.projectStatus || "",
          Task: task.task || "",
          Country: normalizeCountry(task.country || "Unknown"),
          OrderAccepted: task.orderAccepted || "",
          DueDate: task.dueDate || "",
          CompletedOn: task.completedOn || "",
          Analyst1: task.analyst1 || "",
          Analyst2: task.analyst2 || "",
          Income: roundCurrency(income),
          VATPercent: vatRate,
          VATAmount: roundCurrency(vatAmount),
          IncomeIncVAT: roundCurrency(income + vatAmount),
          AllocatedCost: roundCurrency(allocatedCost),
          Profit: roundCurrency(income - allocatedCost),
        });
      });
    });
    const rawDataHeader = [
      "ProjectRef",
      "Client",
      "Company",
      "ProjectStatus",
      "Task",
      "Country",
      "OrderAccepted",
      "DueDate",
      "CompletedOn",
      "Analyst1",
      "Analyst2",
      "Income",
      "VATPercent",
      "VATAmount",
      "IncomeIncVAT",
      "AllocatedCost",
      "Profit",
    ];
    const rawSheet = utils.json_to_sheet(rawRows, { header: rawDataHeader });
    utils.book_append_sheet(workbook, rawSheet, "Raw Tasks");

    const date = new Date();
    const dateStr = date.toISOString().split("T")[0];
    const companyLabel = getCompanyLabel(selectedCompany)
      .replace(/\s+/g, "-")
      .replace(/[^A-Za-z0-9-_]/g, "");
    writeFile(workbook, `Analytics_${companyLabel || "All"}_${dateStr}.xlsx`);
  };

  // Tab change handler with animation
  const handleViewChange = (newView) => {
    setIsAnimating(true);
    setTimeout(() => {
      setViewMode(newView);
      setIsAnimating(false);
    }, 150);
  };

  // Tab definitions
  const tabs = [
    { id: "overview", label: "Overview", icon: <ChartIcon /> },
    { id: "financial", label: "Financial", icon: <DollarSignIcon /> },
    { id: "tasks", label: "Task Analytics", icon: <ClipboardIcon /> },
    { id: "clients", label: "Clients", icon: <UsersIcon /> },
    { id: "geography", label: "Geography", icon: <MapPinIcon /> },
  ];

  // ==================== RENDER ====================
  return (
    <div className="min-h-screen bg-white p-6">
      <HeaderActionsMinimal />
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          className="rounded-2xl shadow-lg border border-gray-200 p-6 sm:p-7 mb-8 relative"
          style={{ background: theme.loginBackground }}
        >
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full mix-blend-overlay filter blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full mix-blend-overlay filter blur-3xl opacity-20 translate-y-1/2 -translate-x-1/2" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/5 pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="relative min-w-0">
              <div className="text-white flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center backdrop-blur-sm border border-white/20 shrink-0">
                  <ChartIcon className="w-7 h-7 text-white" />
                </div>
                <div className="text-left min-w-0 flex flex-col justify-center min-h-[112px] gap-1">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setIsCompanyDropdownOpen(!isCompanyDropdownOpen)}
                      className="flex items-center gap-2 group"
                    >
                      <h1
                        title={getCompanyLabel(selectedCompany)}
                        className="text-[32px] font-extrabold leading-[1.1] tracking-tight group-hover:text-white/80 transition-colors w-full break-words text-left"
                        style={{
                          whiteSpace: "nowrap",
                          maxWidth: "960px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {`Financial Analytics - ${getCompanyLabel(selectedCompany)}`}
                      </h1>
                      <ChevronDown
                        className={`w-5 h-5 text-white/50 group-hover:text-white/80 transition-all duration-200 ${isCompanyDropdownOpen ? "rotate-180" : ""
                          }`}
                      />
                    </button>
                  </div>
                  <div className="flex items-center gap-3 text-white/70">
                    <span className="bg-white/10 px-3 py-1 rounded-full text-sm font-medium">
                      FY {selectedYear}
                    </span>
                    <span className="text-white/40">•</span>
                    <span className="text-sm">{totalProjects} Projects</span>
                    <span className="text-white/40">•</span>
                    <span className="text-sm">{totalTasks} Tasks</span>
                  </div>
                </div>
              </div>

              {isCompanyDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsCompanyDropdownOpen(false)}
                  />
                  <div className="absolute top-full left-0 mt-3 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50 min-w-[280px]">
                    <div className="sticky top-0 bg-gray-50 px-4 py-2 border-b border-gray-100">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Select a company
                      </p>
                    </div>
                    {["All", ...companyOptions].map((companyKey) => (
                      <button
                        key={companyKey}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCompany(companyKey);
                          setIsCompanyDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-b-0 ${selectedCompany === companyKey
                            ? "bg-indigo-50 text-indigo-700 font-semibold"
                            : "text-gray-700"
                          }`}
                      >
                        {getCompanyLabel(companyKey)}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Year
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="All">All</option>
                {availableYears.map((year, index) => (
                  <option key={index} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client
              </label>
              <select
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="All">All</option>
                {[
                  ...new Set(allData.map((item) => item.client || "Unknown")),
                ].map((client, index) => (
                  <option key={index} value={client}>
                    {client}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <select
                value={selectedCountry}
                onChange={(e) =>
                  setSelectedCountry(
                    e.target.value === "All"
                      ? "All"
                      : normalizeCountry(e.target.value)
                  )
                }
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="All">All</option>
                {[
                  ...new Set(
                    allData
                      .flatMap((item) =>
                        (item.tasks || []).map((task) =>
                          normalizeCountry(task.country || "Unknown")
                        )
                      )
                      .filter((country) => country)
                  ),
                ].map((country, index) => (
                  <option key={index} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="All">All</option>
                {[
                  ...new Set(
                    allData
                      .flatMap((item) =>
                        (item.tasks || []).map((task) => task.task || "Unknown")
                      )
                      .filter((type) => type)
                  ),
                ].map((type, index) => (
                  <option key={index} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={exportToExcel}
                className="w-full px-3 py-2 text-white rounded-lg shadow-sm hover:opacity-90 transition-all duration-200 font-medium flex items-center justify-center gap-2"
                style={{ background: theme.loginBackground }}
              >
                <DownloadIcon />
                Export to Excel
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <SummaryCard
            title="Total Revenue"
            value={totalIncome}
            icon={<WalletIcon className="w-4 h-4 text-green-600" />}
            color="green"
            progressPercent={100}
          />
          <SummaryCard
            title="Total Costs"
            value={totalCost}
            icon={<TrendingDownIcon className="w-4 h-4 text-red-600" />}
            color="red"
            progressPercent={
              totalIncome > 0 ? (totalCost / totalIncome) * 100 : 0
            }
          />
          <SummaryCard
            title="Gross Profit"
            value={totalProfit}
            icon={<DollarSignIcon className="w-4 h-4 text-blue-600" />}
            color="blue"
            progressPercent={
              totalIncome > 0 ? (totalProfit / totalIncome) * 100 : 0
            }
          />
          <SummaryCard
            title="Profit Margin"
            value={totalProfitMargin}
            icon={<TrendingUpIcon className="w-4 h-4 text-indigo-600" />}
            color="indigo"
            format="percent"
          />
          <SummaryCard
            title="Total Projects"
            value={totalProjects}
            icon={<CubeIcon className="w-4 h-4 text-purple-600" />}
            color="purple"
            format="number"
          />
          <SummaryCard
            title="Total Tasks"
            value={totalTasks}
            icon={<ClipboardIcon className="w-4 h-4 text-cyan-600" />}
            color="cyan"
            format="number"
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="border-b border-gray-100 p-2">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleViewChange(tab.id)}
                  className={`px-4 py-2.5 font-medium rounded-lg transition-all duration-200 flex items-center gap-2 ${viewMode === tab.id
                      ? "bg-indigo-50 text-indigo-600 border border-indigo-200"
                      : "bg-white text-gray-600 hover:bg-gray-50 border border-transparent"
                    }`}
                >
                  <span
                    className={
                      viewMode === tab.id ? "text-indigo-600" : "text-gray-400"
                    }
                  >
                    {tab.icon}
                  </span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div
            className={`p-8 transition-all duration-300 ${isAnimating
                ? "opacity-0 transform scale-95"
                : "opacity-100 transform scale-100"
              }`}
          >
            {/* OVERVIEW TAB */}
            {viewMode === "overview" && (
              <div className="space-y-8">
                {/* Monthly Trends Chart */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <span className="bg-white p-2 rounded-lg border border-gray-200">
                      <ChartIcon className="w-4 h-4 text-gray-600" />
                    </span>
                    Monthly Financial Trends
                  </h4>
                  <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={monthlyData}>
                        <defs>
                          <linearGradient
                            id="colorIncome"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="#10B981"
                              stopOpacity={0.3}
                            />
                            <stop
                              offset="95%"
                              stopColor="#10B981"
                              stopOpacity={0}
                            />
                          </linearGradient>
                          <linearGradient
                            id="colorCost"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="#EF4444"
                              stopOpacity={0.3}
                            />
                            <stop
                              offset="95%"
                              stopColor="#EF4444"
                              stopOpacity={0}
                            />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="month" stroke="#9CA3AF" />
                        <YAxis
                          stroke="#9CA3AF"
                          tickFormatter={formatCurrency}
                          width={90}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ paddingTop: "20px" }} />
                        <Area
                          type="monotone"
                          dataKey="income"
                          name="Income"
                          stroke="#10B981"
                          fill="url(#colorIncome)"
                          strokeWidth={2}
                        />
                        <Area
                          type="monotone"
                          dataKey="cost"
                          name="Cost"
                          stroke="#EF4444"
                          fill="url(#colorCost)"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="profit"
                          name="Profit"
                          stroke="#6366F1"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Project Status Distribution */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-6">
                    Project Status Distribution
                  </h4>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={projectStatusData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={1}
                        >
                          {projectStatusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip format="number" />} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Quick Insights */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <h5 className="font-semibold text-gray-700 mb-3">
                      Top 5 Clients
                    </h5>
                    <div className="space-y-2">
                      {clientData.slice(0, 5).map((client, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-gray-600 truncate">
                            {client.name}
                          </span>
                          <span className="font-medium text-gray-900">
                            {formatCurrency(client.income)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <h5 className="font-semibold text-gray-700 mb-3">
                      Top 5 Countries
                    </h5>
                    <div className="space-y-2">
                      {countryData.slice(0, 5).map((country, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm">
                          <span className="text-gray-600 truncate flex items-center gap-2 min-w-0">
                            <CountryFlagIcon country={country.name} size={16} />
                            <span className="truncate">{country.name}</span>
                          </span>
                          <span className="font-medium text-gray-900">
                            {formatCurrency(country.income)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <h5 className="font-semibold text-gray-700 mb-3">
                      Top 5 Task Types
                    </h5>
                    <div className="space-y-2">
                      {taskTypeData.slice(0, 5).map((type, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-gray-600 truncate">
                            {type.type}
                          </span>
                          <span className="font-medium text-gray-900">
                            {formatCurrency(type.income)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* FINANCIAL TAB */}
            {viewMode === "financial" && (
              <div className="space-y-8">
                {/* Task Type Profitability */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-6">
                    Task Type Profitability
                  </h4>
                  <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={taskTypeData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="type" stroke="#9CA3AF" />
                        <YAxis
                          stroke="#9CA3AF"
                          tickFormatter={formatCurrency}
                          width={90}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar
                          dataKey="income"
                          name="Income"
                          fill="#10B981"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Monthly Financial Table */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-6">
                    Monthly Financial Details
                  </h4>
                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="min-w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Month
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Income
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Cost
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Profit
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Margin %
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {monthlyData.map((month, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                              {month.month}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {formatCurrency(month.income)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {formatCurrency(month.cost)}
                            </td>
                            <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                              {formatCurrency(month.profit)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {month.profitMargin.toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Invoice Metrics */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-6">
                    Invoice Collection Metrics
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <p className="text-sm text-gray-600 mb-1">
                        Avg Days to Payment
                      </p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {invoiceMetrics.avgDaysToPayment.toFixed(0)}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <p className="text-sm text-gray-600 mb-1">
                        Paid Invoices
                      </p>
                      <p className="text-2xl font-semibold text-green-600">
                        {invoiceMetrics.paidCount}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <p className="text-sm text-gray-600 mb-1">
                        Outstanding Invoices
                      </p>
                      <p className="text-2xl font-semibold text-amber-600">
                        {invoiceMetrics.outstandingCount}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TASK ANALYTICS TAB */}
            {viewMode === "tasks" && (
              <div className="space-y-8">
                {/* Task Completion Status */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-6">
                    Task Completion Status
                  </h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={taskCompletionData.distribution}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            paddingAngle={1}
                          >
                            {taskCompletionData.distribution.map(
                              (entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.fill} />
                              )
                            )}
                          </Pie>
                          <Tooltip
                            content={<CustomTooltip format="number" />}
                          />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex flex-col justify-center space-y-4">
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">
                          On-Time Completion Rate
                        </p>
                        <p className="text-2xl font-semibold text-green-600">
                          {taskCompletionData.metrics.onTimeRate.toFixed(1)}%
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">
                          Average Completion Time
                        </p>
                        <p className="text-2xl font-semibold text-blue-600">
                          {taskCompletionData.metrics.avgCompletionDays.toFixed(
                            0
                          )}{" "}
                          days
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Income by Task Type */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-6">
                    Income by Task Type
                  </h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="h-64">
                      {(() => {
                        const pieData = makePieData(taskTypeData, {
                          nameKey: "type",
                          valueKey: "income",
                          maxSlices: 8,
                          minPercent: 0.035,
                        });
                        const colors = generateColors(pieData.length);
                        return (
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={pieData}
                                dataKey="income"
                                nameKey="type"
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={90}
                                paddingAngle={1}
                              >
                                {pieData.map((_, i) => (
                                  <Cell key={i} fill={colors[i]} />
                                ))}
                              </Pie>
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (!active || !payload?.length) return null;
                                  const data = payload[0].payload;
                                  return (
                                    <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                                      <p className="font-semibold text-gray-900 mb-1">
                                        {data.type}
                                      </p>
                                      <p className="text-sm text-gray-700">
                                        {formatCurrency(data.income)}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        {data._percent.toFixed(1)}% of total
                                      </p>
                                    </div>
                                  );
                                }}
                              />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        );
                      })()}
                    </div>
                    <div className="flex flex-col justify-center space-y-4">
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">
                          Top Earning Task Type
                        </p>
                        <p className="text-2xl font-semibold text-indigo-600">
                          {taskTypeData[0]?.type || "N/A"}
                        </p>
                        <p className="text-sm text-gray-500">
                          {taskTypeData[0]
                            ? formatCurrency(taskTypeData[0].income)
                            : ""}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">
                          Top 3 Concentration
                        </p>
                        <p className="text-2xl font-semibold text-green-600">
                          {(() => {
                            const total = taskTypeData.reduce(
                              (sum, t) => sum + t.income,
                              0
                            );
                            const top3 = taskTypeData
                              .slice(0, 3)
                              .reduce((sum, t) => sum + t.income, 0);
                            return total > 0
                              ? `${((top3 / total) * 100).toFixed(1)}%`
                              : "0%";
                          })()}
                        </p>
                        <p className="text-sm text-gray-500">
                          of total income
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">
                          Number of Task Types
                        </p>
                        <p className="text-2xl font-semibold text-blue-600">
                          {taskTypeData.length}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Analyst Performance */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-6">
                    Analyst Performance
                  </h4>
                  <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analystData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="name" stroke="#9CA3AF" />
                        <YAxis
                          stroke="#9CA3AF"
                          tickFormatter={formatCurrency}
                          width={90}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar
                          dataKey="income"
                          name="Revenue"
                          fill="#6366F1"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Analyst Table */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-6">
                    Analyst Details
                  </h4>
                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="min-w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Analyst
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Tasks
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Revenue
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Avg/Task
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Completion
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {analystData.map((analyst, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                              {analyst.name}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {analyst.taskCount}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {formatCurrency(analyst.income)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {formatCurrency(analyst.avgIncome)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {analyst.completionRate.toFixed(0)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* CLIENTS TAB */}
            {viewMode === "clients" && (
              <div className="space-y-8">
                {/* Client Revenue Chart */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-6">
                    Revenue by Client
                  </h4>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="col-span-2 h-96">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={clientData.slice(0, 15)}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#E5E7EB"
                          />
                          <XAxis
                            dataKey="name"
                            stroke="#9CA3AF"
                            angle={-45}
                            textAnchor="end"
                            height={100}
                            interval={0}
                            tickFormatter={(value) => truncateLabel(value)}
                          />
                          <YAxis
                            stroke="#9CA3AF"
                            tickFormatter={formatCurrency}
                            width={80}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar
                            dataKey="income"
                            name="Income"
                            fill="#6366F1"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="h-96 flex flex-col items-center">
                      {(() => {
                        const pieData = makePieData(clientData, {
                          nameKey: "name",
                          valueKey: "total",
                          maxSlices: 8,
                          minPercent: 0.035,
                        });
                        const colors = generateColors(pieData.length);
                        return (
                          <>
                            <div className="w-full h-1/2">
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={pieData}
                                    dataKey="total"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={90}
                                    paddingAngle={1}
                                  >
                                    {pieData.map((_, i) => (
                                      <Cell key={i} fill={colors[i]} />
                                    ))}
                                    <Label
                                      position="center"
                                      className="text-xs fill-gray-500"
                                    >
                                      Client Share
                                    </Label>
                                  </Pie>
                                  <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                            <PieSideLegend
                              data={pieData}
                              colors={colors}
                              nameKey="name"
                              valueKey="total"
                            />
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* All Clients Table */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-6">
                    All Clients
                  </h4>
                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="min-w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            #
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Client
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Projects
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Revenue
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Cost
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Profit
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Margin
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {clientData.map((client, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-4 text-sm font-medium text-gray-500">
                              {idx + 1}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                              {client.name}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {client.projectCount}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {formatCurrency(client.income)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {formatCurrency(client.cost)}
                            </td>
                            <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                              {formatCurrency(client.profit)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {client.profitMargin.toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* GEOGRAPHY TAB */}
            {viewMode === "geography" && (
              <div className="space-y-8">
                {/* Revenue by Country */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-6">
                    Revenue by Country
                  </h4>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="col-span-2 h-96">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={countryData}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#E5E7EB"
                          />
                          <XAxis
                            dataKey="name"
                            stroke="#9CA3AF"
                            angle={-45}
                            textAnchor="end"
                            height={100}
                            interval={0}
                          />
                          <YAxis
                            stroke="#9CA3AF"
                            tickFormatter={formatCurrency}
                            width={90}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Bar
                            dataKey="income"
                            name="Income"
                            fill="#10B981"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="h-96 flex flex-col items-center">
                      {(() => {
                        const pieData = makePieData(countryData, {
                          nameKey: "name",
                          valueKey: "total",
                          maxSlices: 8,
                          minPercent: 0.035,
                        });
                        const colors = generateColors(pieData.length);
                        return (
                          <>
                            <div className="w-full h-1/2">
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={pieData}
                                    dataKey="total"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={90}
                                    paddingAngle={1}
                                  >
                                    {pieData.map((_, i) => (
                                      <Cell key={i} fill={colors[i]} />
                                    ))}
                                    <Label
                                      position="center"
                                      className="text-xs fill-gray-500"
                                    >
                                      Country Share
                                    </Label>
                                  </Pie>
                                  <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                            <PieSideLegend
                              data={pieData}
                              colors={colors}
                              nameKey="name"
                              valueKey="total"
                            />
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Country Details Table */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-6">
                    Country Details
                  </h4>
                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="min-w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            #
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Country
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Tasks
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Revenue
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Cost
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                            Profit
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {countryData.map((country, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-4 text-sm font-medium text-gray-500">
                              {idx + 1}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                              <span className="flex items-center gap-2">
                                <CountryFlagIcon country={country.name} size={18} />
                                <span>{country.name}</span>
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {country.count}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {formatCurrency(country.income)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {formatCurrency(country.cost)}
                            </td>
                            <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                              {formatCurrency(country.profit)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
